import Swiper from 'swiper'
import { A11y, Keyboard, Navigation } from 'swiper/modules'

import Component, { loadComponent } from '../../../assets/scripts/modules/component.js'
import { getWatchedEpisodeIds } from '../../../assets/scripts/modules/localstorage.js'
import applyMappingsToClone from '../../../assets/scripts/utilities/apply-mappings-to-clone.js'
import findParentBySelector from '../../../assets/scripts/utilities/find-parent-by-selector.js'

const SWIPER_OPTIONS = {
  createElements: true,
  slidesPerView: 1,
  spaceBetween: 16,
  loop: false,
  modules: [Navigation, Keyboard, A11y],
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  keyboard: {
    enabled: true,
    onlyInViewport: true,
    pageUpDown: false,
  },
  a11y: {
    enabled: true,
    scrollOnFocus: true,
    containerMessage: 'Carousel met personen',
    firstSlideMessage: 'Dit is de eerste dia',
    lastSlideMessage: 'Dit is de laatste dia',
    nextSlideMessage: 'Volgende dia',
    prevSlideMessage: 'Vorige dia',
    paginationBulletMessage: 'Ga naar dia {{index}}',
    slideLabelmessage: '{{index}} / {{slidesLength}}',
  },
}

export default class EpisodeCardCarouselComponent extends Component {
  init() {
    this.swiperContainer = this.element.querySelector('.swiper')
    this.slideContainer = this.element.querySelector('.swiper-wrapper')
    this.template = this.element.querySelector('.episode-card-carousel__template')

    if (!this.swiperContainer || !this.slideContainer || !this.template) {
      return
    }

    this.startEpisodeId = window.location.hash.replace('#', '') || ''
    this.characterId = this.element.getAttribute('data-character-id') || ''
    this.swiper = null
    this.swiperOptions = { ...SWIPER_OPTIONS }

    window.addEventListener('fetch-data', event => this.onFetchData(event))
    this.element.reinitAfterFetch = () => this.onFetchData({ detail: this.data })
    window.addEventListener('episode-watched', () => this.onEpisodesUpdated())
  }

  onFetchData(event = { detail: null }) {
    if (!event?.detail) {
      return
    }

    this.data = { ...event.detail }

    const orderMap = new Map((this.data.character_order || []).map((id, index) => [id, index]))

    this.characters = this.data.characters?.length ? [...this.data.characters] : []
    this.characters.sort((a, b) => (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity)) // Sort characters based on order

    this.episodes = this.data.episodes?.length ? [...this.data.episodes].sort((a, b) => a.id - b.id) : []

    if (this.characterId) {
      // Keep only episodes of the specified character
      this.episodes = (this.characters.filter(character => character.id === this.characterId)[0]?.episodes || []).map(episodeId => episodeId && this.episodes.filter(episode => episode.id === episodeId)[0])
    } else {
      // Continue watching episodes
      const episodes = []
      const watchedEpisodeIds = getWatchedEpisodeIds()
      const followedCharacters = this.characters.filter(character => character.episodes.some(id => watchedEpisodeIds.includes(id)))

      followedCharacters.forEach(character => {
        const characterEpisodes = character.episodes.map(episodeId => this.episodes.find(episode => episode.id === episodeId))
        const lastWatchedEpisodeIndex = characterEpisodes.findIndex((episode, i, arr) => watchedEpisodeIds.includes(episode.id) && (!arr[i + 1] || !watchedEpisodeIds.includes(arr[i + 1].id)))
        const nextEpisode = characterEpisodes.slice(lastWatchedEpisodeIndex + 1).find(episode => episode.is_released && episode.id.indexOf('de-opmars-') !== 0) // Find the next episode that isn't a 'de-opmars' episode.

        if (nextEpisode) {
          episodes.push(nextEpisode)
        }
      })

      this.episodes = episodes
    }

    this.render()
  }

  render() {
    this.swiperContainer.setAttribute('hidden', 'hidden')

    if (this.slideContainer.children.length) {
      ;[...this.slideContainer.children].forEach(child => child.remove())
    }

    // If this is a "Keep watching" carrousel, and there are no episodes, then hide
    if (!this.characterId && this.episodes.length === 0) {
      const parent = findParentBySelector(this.element, '.episode-carousel')
      parent?.setAttribute('hidden', 'hidden')

      return
    }

    this.episodes.forEach(episode => {
      const clone = this.template.content.cloneNode(true)

      const episodeBelongsToCharacter = episode.id.indexOf(this.characterId) === 0
      const episodeIsNewsEpisode = episode.id.indexOf('de-opmars-') === 0
      const episodeCharacter = this.characterId ? this.characters.find(character => character.id === this.characterId) : this.characters.find(character => character.id === episode.character_id)

      const mappings = [
        { selector: '.episode-card', values: { id: `episode-card-${episode.id}`, dataset: { episodeId: this.characterId ? episode.id : '', videoMark: episodeBelongsToCharacter ? episode.video_mark : '' }, classList: ['episode-card', ...(episodeIsNewsEpisode ? ['episode-card--news'] : []), ...(episode.watched ? ['episode-card--watched'] : []), ...(episode.is_released ? ['episode-card--released'] : [])], ...(episode.is_released ? { href: this.characterId ? this._getHrefForEpisode(episode) : `${episodeCharacter.url}#episode-card-${episode.id}` } : { href: '?' }) } },
        { selector: '.episode-card__image', values: { src: episode.is_released ? episode.image : '/static/media/placeholders/episode.unreleased.jpg', alt: episode.title, loading: 'auto' } },
        { selector: '.button--pretitle .button__span', values: { textContent: episodeIsNewsEpisode ? episode.display_date : `${episode.pretitle} (${episode.display_date})` } },
        { selector: '.episode-card__title', values: { textContent: episode.title } },
        { selector: '.episode-card__description', values: { textContent: episode.description } },
      ]

      if (!this.characterId) {
        mappings.push({ selector: '.episode-card__profile-title', values: { textContent: episodeCharacter.title } }, { selector: 'img.episode-card__profile-image', values: { src: episodeCharacter.thumbnail, alt: episodeCharacter.title } })
      }

      applyMappingsToClone(mappings, clone)

      this.slideContainer.appendChild(clone)
      this.swiperContainer.removeAttribute('hidden')
    })

    if (this.swiper) {
      this.swiper.update()
    } else {
      this.swiper = new Swiper(this.swiperContainer, this.swiperOptions)
      this.swiper.on('slidesUpdated', () => this.onSwiperActiveIndexChange())
      this.swiper.on('activeIndexChange', () => this.onSwiperActiveIndexChange())
      this.swiper.on('slideChangeTransitionEnd', () => this.onSwiperSlideChangeTransitionEnd())
      this.swiper.update()
    }
  }

  onSwiperSlidesUpdated() {
    const slide = this.swiper.slides[this.swiper.activeIndex]
    const videoMark = slide?.children[0]?.getAttribute('data-video-mark')

    if (!videoMark || videoMark === '') {
      return
    }

    if (document.querySelector('.hero-header')) {
      window.dispatchEvent(new CustomEvent('video-mark-changed', { detail: videoMark }))

      if (this.startEpisodeId) {
        const index = this.swiper.slides.indexOf(this.swiper.slides.find(slide => slide.children[0].id === this.startEpisodeId))

        if (index) {
          this.swiper.slideTo(index)
        }

        this.startEpisodeId = null
      }
    }
  }

  onSwiperActiveIndexChange() {
    this.onSwiperSlidesUpdated()
  }

  onSwiperSlideChangeTransitionEnd() {
    // This makes the browser scroll to the bottom on every carrousel swipe
    //const slide = this.swiper.slides[this.swiper.activeIndex]
    //
    //if (slide.children[0]?.id) {
    //  location.hash = `#${slide.children[0].id}`
    //}
  }

  slideToEpisodeById(episodeId) {
    const slides = this.slideContainer.children
    let targetIndex = -1

    for (let i = 0; i < slides.length; i++) {
      if (slides[i].children[0]?.dataset?.episodeId === episodeId) {
        targetIndex = i
        break
      }
    }

    if (targetIndex !== -1 && this.swiper) {
      this.swiper.slideTo(targetIndex)
    }
  }

  onEpisodesUpdated() {
    this.render()

    const firstNonWatchedEpisode = this.episodes.find(episode => !episode.watched)

    if (firstNonWatchedEpisode) {
      this.slideToEpisodeById(firstNonWatchedEpisode.id)
    }
  }

  _getHrefForEpisode(episode) {
    // NOTE: This isn't really ideal because it won't auto-start the tour and the user will have to select it manually. Best effort; this URL won't be used apart from clicking 'open in new tab'
    return episode?.story?.type === 'video' ? episode?.story?.url : episode?.story?.type === 'micrio' ? `https://i.micr.io/${episode?.story?.id}` : '?'
  }
}

window.addEventListener('init-load', () => loadComponent(document, 'episode-card-carousel', EpisodeCardCarouselComponent))
